import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContactUsService } from 'src/app/core/services/contact-us.service';
import { SnackbarService } from 'src/app/core/services/snackbar.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { UserService } from 'src/app/core/services/user-service.service';
import { IDropdownOption, USER_TYPES } from 'src/app/core/utils/dropdownConstant';
import { BreadcrumbItem } from 'src/app/shared/breadcrumb/breadcrumb.component';

@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss']
})
export class ContactUsComponent implements OnInit {
  contactForm!: FormGroup;
  isLoggedIn = false;

  // Breadcrumb configuration
  breadcrumbItems: BreadcrumbItem[] = [
    { name: 'Home', isActive: true, route: '/' },
    { name: 'Get in Touch', isActive: false }
  ];

  userTypes: IDropdownOption[] = USER_TYPES;

  constructor(
    private fb: FormBuilder,
    private contactUsService: ContactUsService,
    private snackbarService: SnackbarService,
    private authService: AuthService,
    private userService: UserService
  ) { }

  ngOnInit() {
    this.initForm();
    this.checkUserLoggedIn();
  }

  initForm() {
    this.contactForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
      email: ['', [Validators.required, Validators.email, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')]],
      userType: ['', Validators.required],
      message: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(500)]]
    });
  }

  checkUserLoggedIn() {
    this.isLoggedIn = this.authService.checkAuthState();

    if (this.isLoggedIn) {
      // Try to get user data from sessionStorage first
      try {
        const userData = JSON.parse(sessionStorage.getItem('user') || '');
        if (userData && this.isValidUserData(userData)) {

          this.prefillFormWithUserData(userData);
        } else {
          this.fetchUserProfile();
        }
      } catch (error) {
        // If sessionStorage data is invalid or missing, fetch from API
        this.fetchUserProfile();
      }
    }
  }

  fetchUserProfile() {
    this.userService.getUserProfile().subscribe({
      next: (response) => {
        if (response.data) {
          this.prefillFormWithUserData(response.data);
        }
      },
      error: (err) => {
        console.error('Error fetching user profile:', err);
      }
    });
  }

  prefillFormWithUserData(userData: any) {
    if (userData) {
      // Patch the form with user data
      this.contactForm.patchValue({
        name: userData.name || userData.firstName + ' ' + userData.lastName || '',
        email: userData.email || '',
        userType: userData.userType || ''
      });

      // Disable the name and email fields since they're pre-filled from user data
      if (this.isLoggedIn) {
        this.contactForm.get('name')?.disable();
        this.contactForm.get('email')?.disable();
        this.contactForm.get('userType')?.disable();
      }
    }
  }

  onSubmit() {
    // Mark all fields as touched to trigger validation messages
    Object.keys(this.contactForm.controls).forEach(key => {
      const control = this.contactForm.get(key);
      control?.markAsTouched();
    });

    // Ensure the userType field is properly validated
    this.validateUserType();

    // Check if the form is valid before proceeding
    if (this.contactForm.invalid) {
      return;
    }

    // Create a complete form value object that includes disabled controls
    const formValue = {
      ...this.contactForm.getRawValue()
    };

    this.contactUsService.sendContactUs(formValue).subscribe({
      next: (res) => {
        this.snackbarService.showSuccess(res.message || 'Message sent successfully!');

        // Reset the form without triggering validation
        this.contactForm.reset();

        // Mark all fields as pristine and untouched to prevent validation errors
        Object.keys(this.contactForm.controls).forEach(key => {
          const control = this.contactForm.get(key);
          control?.markAsPristine();
          control?.markAsUntouched();
        });

        // Re-check login status after form submission to properly handle fields
        this.checkUserLoggedIn();
      },
      error: (err) => {
        this.snackbarService.showError(err.error?.message || 'Failed to send message. Please try again.');
      }
    });
  }

  // Validate that userData has all the required keys for form prefilling
  isValidUserData(userData: any): boolean {
    // Check for required fields: name/firstName, email, userType
    const hasName = !!userData.name;
    const hasEmail = !!userData.email;
    const hasUserType = !!userData.userType;

    return hasName && hasEmail && hasUserType;
  }

  validateUserType() {
    const userTypeControl = this.contactForm.get('userType');

    // If user is logged in, the field is disabled and should be considered valid
    if (this.isLoggedIn) {
      return;
    }

    // For non-logged-in users, ensure userType is selected
    if (!userTypeControl?.value) {
      userTypeControl?.setErrors({ required: true });
    } else {
      userTypeControl?.setErrors(null);
    }
  }
}
